import type {
  ButtonProps as MuiButtonProps,
  ButtonTypeMap as MuiButtonTypeMap,
  TooltipProps,
} from "@mui/material";
import { Button as MuiButton, Tooltip } from "@mui/material";
import type { MouseEventHandler } from "react";
import { useCallback, useState } from "react";

import * as sx from "./index.sx";

/**
 * Generics copied from Mui implementation of ButtonProps - without the generics
 * we lose support for the `component` prop
 */
export type ButtonProps<
  D extends React.ElementType = MuiButtonTypeMap["defaultComponent"],
  P = object,
> = MuiButtonProps<D, P> & {
  tooltipPlacement?: TooltipProps["placement"];
  tooltip?: TooltipProps["title"];
  onClick?: MouseEventHandler<HTMLButtonElement>;
  showLoadingSpinner?: boolean;
};

/**
 * This is basically just the MUI Button component with an added `tooltip` prop
 * that abstracts away the adding of the wrapper element.
 */
export function Button<
  D extends React.ElementType = MuiButtonTypeMap["defaultComponent"],
  P = object,
>({
  onClick,
  showLoadingSpinner,
  tooltip,
  tooltipPlacement,
  ...props
}: ButtonProps<D, P>) {
  const [handlingClick, setHandlingClick] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      setHandlingClick(true);
      try {
        await onClick?.(e);
      } finally {
        setHandlingClick(false);
      }
    },
    [onClick, setHandlingClick],
  );

  return (
    <Tooltip placement={tooltipPlacement} title={tooltip ?? ""}>
      {/* Need to add a span here or else the Tooltip complains when the button is disabled */}
      <sx.Container fullWidth={props.fullWidth}>
        <MuiButton
          loading={showLoadingSpinner ? handlingClick : undefined}
          {...props}
          // handlingClick is necessary here in case showLoadingSpinner is falsey
          disabled={props.disabled || handlingClick}
          onClick={handleClick}
        />
      </sx.Container>
    </Tooltip>
  );
}
